import React from 'react';

import type { IconName } from './DynamicIcon';
import { DynamicIcon } from './DynamicIcon';
import { usePermissionsDebug } from '@personnel/presentation/hooks/usePermissionsDebug';

/**
 * Permission Group Component
 *
 * Visual grouping container for permissions without a master switch.
 * All permissions inside are independent.
 *
 * @param {PermissionGroupProps} props - Component properties
 * @returns {React.ReactElement} Permission group component
 *
 * @example
 * <PermissionGroup
 *   name="Módulos a Nivel Organización"
 *   description="Permite acceder a los módulos..."
 *   iconName="monitor"
 * >
 *   <PermissionCheckbox ... />
 *   <PermissionCheckbox ... />
 * </PermissionGroup>
 */

interface PermissionGroupProps {
  /** Group name */
  name: string;
  /** Optional description */
  description?: string;
  /** Icon name (optional) */
  iconName?: IconName;
  /** Child permissions */
  children: React.ReactNode;
}

const styles = {
  container: 'flex flex-col gap-3',
  header: {
    wrapper: 'flex items-start gap-3',
    icon: 'size-5 text-foreground mt-0.5 shrink-0',
    textWrapper: 'flex flex-col gap-1',
    title: 'text-sm font-medium text-foreground',
    description: 'text-xs text-muted-foreground whitespace-pre-line',
  },
  content: 'flex flex-col gap-2 ml-8',
  debug: 'text-xs font-mono p-2 rounded-md bg-green-50 border border-green-200 text-green-700 mt-1 inline-block',
};

export const PermissionGroup = ({
  name,
  description,
  iconName,
  children,
}: PermissionGroupProps): React.ReactElement => {
  const { isDebugMode } = usePermissionsDebug();

  return (
    <div className={styles.container}>
      {/* Group Header */}
      <div className={styles.header.wrapper}>
        {iconName && (
          <div className={styles.header.icon}>
            <DynamicIcon className="size-5 text-foreground" name={iconName} />
          </div>
        )}

        <div className={styles.header.textWrapper}>
          <span className={styles.header.title}>{name}</span>
          {description && <p className={styles.header.description}>{description}</p>}
          {/* Debug info - Only shown in development when enabled via console */}
          {isDebugMode && <span className={styles.debug}>📁 GROUP</span>}
        </div>
      </div>
      {/* Permissions List (indented) */}
      <div className={styles.content}>{children}</div>
    </div>
  );
};
